import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  BackWallTypeDetailResponse,
  BackWallTypeForm,
  BackWallTypeListResponse,
  BackWallTypeUpdatePayload,
} from "../types";
import {
  selectBackWallTypeForm,
  selectBackWallTypePage,
  selectBackWallTypePerPage,
} from "./selector";
import { backWallTypeActions } from "./slice";

let isFetchingList = false;

function* handleGetBackWallTypeList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectBackWallTypePage);
    const perPage: number = yield select(selectBackWallTypePerPage);

    const response: BackWallTypeListResponse = yield call(
      request,
      `${API_URL}/back-wall/list?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const rawData = response?.data || [];
    const meta = getPaginationMeta(page, perPage, rawData, response?.meta);
    const data = rawData.map((item) => ({
      id: item.backWallCode,
      backWallCode: item.backWallCode,
      backWallDesc: item.backWallDesc,
      bump: item.bump,
    }));
    const payload = { data, meta };

    if (page === 1) {
      yield put(backWallTypeActions.getBackWallTypeListSuccess(payload));
    } else {
      yield put(backWallTypeActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectBackWallTypePage);

    if (page === 1) {
      yield put(backWallTypeActions.getBackWallTypeListError(message));
    } else {
      yield put(backWallTypeActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

function* handleAddBackWallType() {
  const form: BackWallTypeForm = yield select(selectBackWallTypeForm);

  try {
    const response = yield call(request, `${API_URL}/back-wall`, {
      method: "POST",
      body: JSON.stringify({
        backWallCode: form.backWallCode.trim(),
        backWallDesc: form.backWallDesc.trim(),
        bump: Number(form.bump),
      }),
    });

    yield put(backWallTypeActions.addBackWallTypeSuccess(response.data));
  } catch (error) {
    yield put(
      backWallTypeActions.addBackWallTypeError((error as Error).message)
    );
  }
}

function* handleGetBackWallTypeDetail(action: PayloadAction<string>) {
  const id = action.payload?.trim();

  try {
    const response: BackWallTypeDetailResponse = yield call(
      request,
      `${API_URL}/back-wall/${encodeURIComponent(id)}`,
      { method: "GET" },
    );

    const detail = response?.data;

    yield put(
      backWallTypeActions.getBackWallTypeDetailSuccess({
        backWallCode: detail?.backWallCode ?? "",
        backWallDesc: detail?.backWallDesc ?? "",
        bump: Number(detail?.bump ?? 0),
      })
    );
  } catch (error) {
    yield put(
      backWallTypeActions.getBackWallTypeDetailError((error as Error).message)
    );
  }
}

function* handleUpdateBackWallType(
  action: PayloadAction<BackWallTypeUpdatePayload>
) {
  const { id, backWallDesc, bump } = action.payload;

  try {
    const response: BackWallTypeDetailResponse = yield call(
      request,
      `${API_URL}/back-wall/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          backWallDesc: backWallDesc.trim(),
          bump: Number(bump),
        }),
      },
    );

    const detail = response?.data;

    yield put(
      backWallTypeActions.updateBackWallTypeSuccess({
        backWallCode: detail?.backWallCode ?? id,
        backWallDesc: detail?.backWallDesc ?? backWallDesc,
        bump: Number(detail?.bump ?? bump),
      })
    );
  } catch (error) {
    yield put(
      backWallTypeActions.updateBackWallTypeError((error as Error).message)
    );
  }
}

function* handleDeleteBackWallType(action: PayloadAction<string>) {
  const id = action.payload;

  try {
    yield call(
      request,
      `${API_URL}/back-wall/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );

    yield put(backWallTypeActions.deleteBackWallTypeSuccess(id));
  } catch (error) {
    yield put(
      backWallTypeActions.deleteBackWallTypeError((error as Error).message)
    );
  }
}

function* backWallTypeSaga() {
  yield takeLeading(
    [
      backWallTypeActions.getBackWallTypeList.type,
      backWallTypeActions.getNextPageItems.type,
    ],
    handleGetBackWallTypeList,
  );
  yield takeLatest(
    backWallTypeActions.addBackWallType.type,
    handleAddBackWallType,
  );
  yield takeLatest(
    backWallTypeActions.getBackWallTypeDetail.type,
    handleGetBackWallTypeDetail,
  );
  yield takeLatest(
    backWallTypeActions.updateBackWallType.type,
    handleUpdateBackWallType,
  );
  yield takeLatest(
    backWallTypeActions.deleteBackWallType.type,
    handleDeleteBackWallType,
  );
}

export default backWallTypeSaga;
